import type { ReactNode } from 'react';
import { cx } from '../internal/cx';

export type AlertTone = 'neutral' | 'info' | 'proof' | 'danger' | 'warning';

export interface AlertProps {
  tone?: AlertTone;
  title: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  className?: string;
}

/**
 * `danger` usa `role="alert"` (assertivo — interrompe o leitor de tela, reservado a
 * erro que bloqueia o usuário). Todos os outros tons usam `role="status"` (polido —
 * anuncia sem interromper). Nunca decide o tom pelo texto do consumidor: quem chama
 * o componente escolhe `tone` deliberadamente.
 */
export function Alert({ tone = 'info', title, children, icon, className }: AlertProps) {
  const isAssertive = tone === 'danger';

  return (
    <div
      className={cx('argink-alert', `argink-alert--${tone}`, className)}
      role={isAssertive ? 'alert' : 'status'}
      aria-live={isAssertive ? 'assertive' : 'polite'}
    >
      {icon ? (
        <span className="argink-alert__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <div className="argink-alert__body">
        <p className="argink-alert__title">{title}</p>
        {children ? <div className="argink-alert__description">{children}</div> : null}
      </div>
    </div>
  );
}
